import { keepPreviousData } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { mapAccountsToSelectOptions } from '../../shared/accountSelectOptions'

import type { TaskTemplate } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import { useNominatorGroupOptions } from '@percent/admin-dashboard/app/grants/assignmentRules/hooks/useNominatorGroupOptions'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { mapCountriesToSelectOptions } from '@percent/admin-dashboard/common/utility/mapCountriesToSelectOptions'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useQuery } from '@percent/utility'
import { AddAssignmentRulesForm } from './AddAssignmentRulesForm'

export type GrantsAssignmentRulesFormModalProps = Readonly<{
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}>

export function AddAssignmentRulesFormModal({ open, onClose, onSuccess }: GrantsAssignmentRulesFormModalProps) {
  const { grantProgramService, accountService, countriesService } = useServices()

  const countriesQuery = useQuery(['countries'], countriesService.getCountries, {}, { enabled: open })
  const countries = countriesQuery.data ?? []

  const [selectedProgramId, setSelectedProgramId] = useState<string | undefined>(undefined)
  const [programSearchQuery, setProgramSearchQuery] = useState<string>('')
  const [accountSearchQuery, setAccountSearchQuery] = useState<string>('')

  // Fetch programs only when modal is open
  const grantPrograms = useQuery(
    ['grant-programs', 'all', programSearchQuery],
    grantProgramService.getAllPrograms,
    {
      pageSize: 100,
      ...(programSearchQuery && { search: programSearchQuery })
    },
    { enabled: open, placeholderData: keepPreviousData }
  )

  const [allTaskTemplates, setAllTaskTemplates] = useState<TaskTemplate[]>([])
  const [taskTemplatesError, setTaskTemplatesError] = useState<Error | null>(null)

  useEffect(() => {
    if (!open) {
      setSelectedProgramId(undefined)
      setAllTaskTemplates([])
      setProgramSearchQuery('')
      setAccountSearchQuery('')
    }
  }, [open])

  const fetchTaskTemplates = useCallback(
    (programId: string) => {
      if (!programId) {
        setAllTaskTemplates([])

        return
      }

      setTaskTemplatesError(null)

      grantProgramService
        .getTaskTemplates({ programId })
        .then(result => {
          const templates = (result.data?.data ?? []) as TaskTemplate[]
          setAllTaskTemplates(templates)
        })
        .catch(err => {
          setTaskTemplatesError(err)
        })
    },
    [grantProgramService]
  )

  const taskTemplateOptions = useMemo(() => {
    return allTaskTemplates
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(template => ({
        label: template.name,
        value: template.id
      }))
  }, [allTaskTemplates])

  const countriesOptions = useMemo(() => mapCountriesToSelectOptions(countries), [countries])

  const selectedProgram = grantPrograms.data?.find(p => p.id === selectedProgramId)
  const partnerId = selectedProgram?.partnerId
  const selectedProgramName = selectedProgram?.name

  const { nominatorGroupOptions, isLoading: nominatorGroupsLoading } = useNominatorGroupOptions({
    programId: selectedProgramId,
    programName: selectedProgramName,
    enabled: open && !!selectedProgramId
  })

  const accounts = useQuery(
    ['accounts', 'partner', partnerId || 'none', accountSearchQuery],
    accountService.getAccounts,
    {
      ...(partnerId && { partnerId }),
      ...(accountSearchQuery && { search: accountSearchQuery }),
      pageSize: 1000
    },
    { enabled: open && !!partnerId, placeholderData: keepPreviousData }
  )
  const accountsOptions = useMemo(() => mapAccountsToSelectOptions(accounts.data), [accounts.data])

  const programOptions = useMemo(
    () =>
      grantPrograms.data?.map(p => ({
        label: p.name,
        value: p.id
      })) ?? [],
    [grantPrograms.data]
  )

  const handleSuccess = () => {
    onSuccess?.()
    onClose()
  }

  const error = taskTemplatesError || grantPrograms.error || accounts.error || countriesQuery.error

  if (error && !grantPrograms.data) {
    return <ErrorView errorMessage={error.message} />
  }

  return (
    <AddAssignmentRulesForm
      programs={programOptions}
      selectedProgramId={selectedProgramId}
      onProgramChange={setSelectedProgramId}
      onProgramSelect={fetchTaskTemplates}
      programSearchQuery={programSearchQuery}
      onProgramSearchChange={setProgramSearchQuery}
      programsLoading={grantPrograms.isLoading}
      taskTemplates={taskTemplateOptions}
      countries={countriesOptions}
      accounts={accountsOptions}
      onAccountSearchChange={setAccountSearchQuery}
      accountsLoading={accounts.isLoading}
      nominatorGroups={nominatorGroupOptions}
      nominatorGroupsLoading={nominatorGroupsLoading}
      invalidateQuery={handleSuccess}
      open={open}
      onClose={onClose}
    />
  )
}
